import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import {
  downloadCallRecording,
  fetchCallDetail,
  fetchVapiCallDetail,
  resolveCallRecordingUrl,
  type DownloadedCallRecording,
  type RecordingType,
} from './api'
import type { CallDetail } from '@/features/calls/types'
import { callDetailKeys } from '@/lib/queryKeys'
import { requireAuthToken } from '@/lib/api'
import { RECORDING_URL_MAX_LOAD_FAILURES } from '@/config/constants'

interface UseCallDetailResult {
  callDetail: CallDetail | null
  /**
   * True when the query has no data yet AND a fetch is in flight. After a
   * failed first fetch, `isPending` stays true (data is undefined), so a
   * subsequent `refetch()` flips this to true again. `isLoading` is the
   * right field to drive the Retry button's "Retrying…" state from an
   * error condition.
   */
  isLoading: boolean
  error: string | null
  stereoUrl: string | null
  monoUrl: string | null
  recordingType: RecordingType | null
  /**
   * The audio is a Supabase Storage object, not the Vapi recording.
   * Transcript turns and message events carry Vapi timings, so they do not line
   * up with this audio — consumers hide the transcript/messages strip.
   */
  isSupabaseRecording: boolean
  resolvedRecordingUrl: string | null
  isRecordingUrlLoading: boolean
  /**
   * The query failed AND left us without a recording type, so no signed URL can
   * be obtained. React Query keeps previous data across a failed refetch, so a
   * still-playable recording deliberately does NOT set this.
   */
  recordingBlocked: boolean
  /**
   * Re-run the underlying query. Returns the TanStack Promise so callers
   * can await it if needed, but most consumers fire it for side effect.
   */
  refetch: () => Promise<unknown>
  /**
   * `force` skips the cache and the payload seed and always hits
   * `/recording-url`. R2 presigns carry no expiry we can read, so playback
   * failure is the only signal we trust that one has gone bad — recovery has
   * to ask for a genuinely new URL, not replay the one that just failed.
   */
  resolveRecordingUrl: (
    recordingType: RecordingType,
    options?: { force?: boolean }
  ) => Promise<string>
  prefetchRecordingUrl: (
    recordingType?: RecordingType | null
  ) => Promise<string | null>
  invalidateRecordingUrl: (recordingType: RecordingType) => void
  /**
   * Fetches the recording as a downloadable blob via the same-origin
   * `/recording-download` proxy — see `downloadCallRecording` for why the
   * Download button goes through this instead of `resolvedRecordingUrl`.
   * Resolves to `null` when there's no recording to download at all; throws
   * on a failed fetch so callers can surface the error.
   */
  downloadRecording: () => Promise<DownloadedCallRecording | null>
}

/**
 * A newer resolve took over before this one landed.
 *
 * Benign concurrency, not a failure the user can act on — the replacement is
 * already in flight and will deliver. Callers distinguish it so they don't
 * offer a Retry for a request that was superseded on purpose.
 */
/** Payload marker for audio served from Supabase Storage (see the backend's
 * `SUPABASE_RECORDING_SOURCE`). */
const SUPABASE_RECORDING_SOURCE = 'supabase'

export class SupersededRecordingUrlRequest extends Error {
  constructor() {
    super('Stale recording URL request')
    this.name = 'SupersededRecordingUrlRequest'
  }
}

type RecordingUrlEntry<T> = {
  contextKey: string
  value: T
}

/**
 * Fetches rich call detail data using React Query.
 *
 * source='supabase' → /data/calls/{id}  (live calls, Supabase-enriched)
 * source='vapi'     → /data/vapi/call/{id}  (eval test calls, VAPI-direct)
 */
export function useCallDetail(
  callId: string | null,
  source: 'supabase' | 'vapi',
  polling?: Pick<UseQueryOptions<CallDetail | null, Error>, 'refetchInterval'>
): UseCallDetailResult {
  const { getToken } = useAuth()
  const [recordingUrlCache, setRecordingUrlCache] = useState<
    Partial<Record<RecordingType, RecordingUrlEntry<string>>>
  >({})
  const [recordingUrlLoading, setRecordingUrlLoading] = useState<
    Partial<Record<RecordingType, RecordingUrlEntry<boolean>>>
  >({})
  const [invalidatedRecordingUrlSeeds, setInvalidatedRecordingUrlSeeds] =
    useState<Partial<Record<RecordingType, RecordingUrlEntry<boolean>>>>({})
  const [recordingUrlLoadFailures, setRecordingUrlLoadFailures] = useState<
    Partial<Record<RecordingType, RecordingUrlEntry<number>>>
  >({})
  const recordingUrlPromisesRef = useRef<
    Partial<Record<RecordingType, RecordingUrlEntry<Promise<string>>>>
  >({})

  const query = useQuery<CallDetail | null, Error>({
    queryKey: callDetailKeys.detail(callId, source),
    queryFn: async () => {
      if (!callId) return null
      // Both legs are bounded — the token here, the fetch via AbortSignal in
      // fetchCallDetail. An unbounded hang in either would leave the query
      // pending forever, and the Retry surface this hook feeds is driven by
      // `error`, so it would be unreachable.
      const token = await requireAuthToken(getToken)
      return source === 'supabase'
        ? fetchCallDetail(token, callId)
        : fetchVapiCallDetail(token, callId)
    },
    enabled: Boolean(callId),
    staleTime: 5 * 60 * 1000, // 5 min — call detail rarely changes
    refetchInterval: polling?.refetchInterval,
  })

  const callDetail = query.data ?? null
  const recordingContextKey = `${callId ?? ''}:${callDetail?.provider_call_id ?? ''}`
  const recordingContextKeyRef = useRef(recordingContextKey)
  recordingContextKeyRef.current = recordingContextKey

  // VAPI recording URLs lack CORS headers required for StereoWaveformPlayer's
  // splitChannels mode (AudioContext decode). Force mono for VAPI source so
  // WaveSurfer uses the <audio> element backend instead, which bypasses CORS.
  const stereoUrl =
    source === 'supabase' ? (callDetail?.stereo_recording_url ?? null) : null
  const rawAudioUrl = callDetail?.audio_url ?? null
  const rawStereoUrl = callDetail?.stereo_recording_url ?? null
  const rawMonoUrl = callDetail?.recording_url ?? null
  const audioUrlIsStereoAlias = Boolean(
    rawAudioUrl && rawStereoUrl && rawAudioUrl === rawStereoUrl
  )
  const monoUrl =
    source === 'vapi'
      ? // VAPI: try stereo_recording_url first, then audio_url, then recording_url
        (rawStereoUrl ?? rawAudioUrl ?? rawMonoUrl)
      : (rawMonoUrl ?? (audioUrlIsStereoAlias ? null : rawAudioUrl))
  const isSupabaseRecording =
    callDetail?.recording_source === SUPABASE_RECORDING_SOURCE
  const recordingType: RecordingType | null = monoUrl
    ? 'mono'
    : stereoUrl
      ? 'stereo'
      : null

  useEffect(() => {
    setRecordingUrlCache({})
    setRecordingUrlLoading({})
    setInvalidatedRecordingUrlSeeds({})
    setRecordingUrlLoadFailures({})
    recordingUrlPromisesRef.current = {}
  }, [callId, callDetail?.provider_call_id])

  useEffect(() => {
    setRecordingUrlCache((prev) => {
      const next = { ...prev }
      let changed = false
      if (
        monoUrl &&
        invalidatedRecordingUrlSeeds.mono?.contextKey !== recordingContextKey &&
        next.mono?.contextKey !== recordingContextKey
      ) {
        next.mono = { contextKey: recordingContextKey, value: monoUrl }
        changed = true
      }
      if (
        stereoUrl &&
        invalidatedRecordingUrlSeeds.stereo?.contextKey !==
          recordingContextKey &&
        next.stereo?.contextKey !== recordingContextKey
      ) {
        next.stereo = { contextKey: recordingContextKey, value: stereoUrl }
        changed = true
      }
      return changed ? next : prev
    })
  }, [
    invalidatedRecordingUrlSeeds.mono?.contextKey,
    invalidatedRecordingUrlSeeds.stereo?.contextKey,
    monoUrl,
    recordingContextKey,
    stereoUrl,
  ])

  const resolveRecordingUrl = useCallback(
    async (
      requestedRecordingType: RecordingType,
      options?: { force?: boolean }
    ) => {
      if (!callId) throw new Error('callId is required')
      const force = options?.force ?? false
      const cachedUrl = recordingUrlCache[requestedRecordingType]
      if (!force && cachedUrl?.contextKey === recordingContextKey) {
        return cachedUrl.value
      }
      // Reused even when forced: an in-flight request is always a real
      // `/recording-url` call, so its URL is newer than whatever just failed.
      // This is also what keeps a double-clicked play button to one request.
      const inFlight = recordingUrlPromisesRef.current[requestedRecordingType]
      if (inFlight?.contextKey === recordingContextKey) return inFlight.value
      const seededUrl = requestedRecordingType === 'mono' ? monoUrl : stereoUrl
      if (
        !force &&
        seededUrl &&
        invalidatedRecordingUrlSeeds[requestedRecordingType]?.contextKey !==
          recordingContextKey
      ) {
        setRecordingUrlCache((prev) => ({
          ...prev,
          [requestedRecordingType]: {
            contextKey: recordingContextKey,
            value: seededUrl,
          },
        }))
        return seededUrl
      }

      setRecordingUrlLoading((prev) => ({
        ...prev,
        [requestedRecordingType]: {
          contextKey: recordingContextKey,
          value: true,
        },
      }))
      const providerCallId = callDetail?.provider_call_id
      const requestContextKey = recordingContextKey
      const promise = (async () => {
        const token = await requireAuthToken(getToken)
        return resolveCallRecordingUrl(token, {
          callId,
          providerCallId,
          recordingType: requestedRecordingType,
        })
      })()
      recordingUrlPromisesRef.current[requestedRecordingType] = {
        contextKey: requestContextKey,
        value: promise,
      }
      // Promise identity IS request identity. `contextKey` alone can't order two
      // resolves for the same call — it doesn't change when a URL is
      // invalidated — but every event that supersedes a request already mutates
      // this ref: invalidate deletes the entry, a newer request overwrites it,
      // and switching calls clears the map.
      const isCurrentRequest = () =>
        recordingContextKeyRef.current === requestContextKey &&
        recordingUrlPromisesRef.current[requestedRecordingType]?.value ===
          promise
      try {
        const url = await promise
        if (!isCurrentRequest()) {
          throw new SupersededRecordingUrlRequest()
        }
        setRecordingUrlCache((prev) => ({
          ...prev,
          [requestedRecordingType]: {
            contextKey: requestContextKey,
            value: url,
          },
        }))
        return url
      } finally {
        // Superseded requests must not clean up: the ref and the loading flag
        // now belong to a newer request.
        if (isCurrentRequest()) {
          delete recordingUrlPromisesRef.current[requestedRecordingType]
          setRecordingUrlLoading((prev) => ({
            ...prev,
            [requestedRecordingType]: {
              contextKey: requestContextKey,
              value: false,
            },
          }))
        }
      }
    },
    [
      callDetail?.provider_call_id,
      callId,
      getToken,
      invalidatedRecordingUrlSeeds,
      monoUrl,
      recordingContextKey,
      recordingUrlCache,
      stereoUrl,
    ]
  )

  const downloadRecording = useCallback(async () => {
    if (!callId || !recordingType) return null
    const token = await requireAuthToken(getToken)
    return downloadCallRecording(token, {
      callId,
      providerCallId: callDetail?.provider_call_id,
      recordingType,
    })
  }, [callDetail?.provider_call_id, callId, getToken, recordingType])

  const prefetchRecordingUrl = useCallback(
    async (requestedType?: RecordingType | null) => {
      const type = requestedType ?? recordingType
      if (!type) return null
      const loadFailures = recordingUrlLoadFailures[type]
      if (
        loadFailures?.contextKey === recordingContextKey &&
        loadFailures.value >= RECORDING_URL_MAX_LOAD_FAILURES
      ) {
        return null
      }
      try {
        return await resolveRecordingUrl(type)
      } catch {
        return null
      }
    },
    [
      recordingContextKey,
      recordingType,
      recordingUrlLoadFailures,
      resolveRecordingUrl,
    ]
  )

  const invalidateRecordingUrl = useCallback(
    (invalidatedRecordingType: RecordingType) => {
      setRecordingUrlCache((prev) => {
        const next = { ...prev }
        delete next[invalidatedRecordingType]
        return next
      })
      setInvalidatedRecordingUrlSeeds((prev) => ({
        ...prev,
        [invalidatedRecordingType]: {
          contextKey: recordingContextKey,
          value: true,
        },
      }))
      setRecordingUrlLoadFailures((prev) => {
        const previousCount =
          prev[invalidatedRecordingType]?.contextKey === recordingContextKey
            ? (prev[invalidatedRecordingType]?.value ?? 0)
            : 0
        return {
          ...prev,
          [invalidatedRecordingType]: {
            contextKey: recordingContextKey,
            value: previousCount + 1,
          },
        }
      })
      // Also supersedes any in-flight resolve: with the entry gone it can no
      // longer match on promise identity, so it can't write the URL we just
      // threw away back into the cache.
      delete recordingUrlPromisesRef.current[invalidatedRecordingType]
      // ...but that same supersession makes the in-flight resolve skip its
      // `finally` cleanup, and the loading flag lives in that guarded block. No
      // replacement request is started here, so nothing else would ever clear
      // it — leaving the play button pinned to a disabled spinner. Reset it
      // alongside the other state above.
      setRecordingUrlLoading((prev) => ({
        ...prev,
        [invalidatedRecordingType]: {
          contextKey: recordingContextKey,
          value: false,
        },
      }))
    },
    [recordingContextKey]
  )

  const resolvedRecordingUrl = recordingType
    ? ((recordingUrlCache[recordingType]?.contextKey === recordingContextKey
        ? recordingUrlCache[recordingType]?.value
        : undefined) ??
      (invalidatedRecordingUrlSeeds[recordingType]?.contextKey !==
      recordingContextKey
        ? recordingType === 'mono'
          ? monoUrl
          : stereoUrl
        : null))
    : null

  return {
    callDetail,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    stereoUrl,
    monoUrl,
    recordingType,
    isSupabaseRecording,
    resolvedRecordingUrl,
    // "Settled with still no way to get a URL", not "errored". A 404 resolves
    // to null data with no error, and a payload can come back with every
    // recording field null — both are settled states where `recordingType`
    // never becomes non-null, so gating on `error` alone left the consumer's
    // loading flag unbounded and the play button spinning forever.
    recordingBlocked: query.status !== 'pending' && !recordingType,
    isRecordingUrlLoading: recordingType
      ? Boolean(
          recordingUrlLoading[recordingType]?.contextKey ===
            recordingContextKey && recordingUrlLoading[recordingType]?.value
        )
      : false,
    refetch: query.refetch,
    resolveRecordingUrl,
    prefetchRecordingUrl,
    invalidateRecordingUrl,
    downloadRecording,
  }
}
